/**
 * Movement dev-tool panel. A small always-on window that shows, live:
 *   - which movement keys (WASD) are currently held,
 *   - which camera/aim keys (arrows) are currently held,
 *   - an 8-direction "radar"/joystick map of the current move direction, with
 *     the committed facing (amber needle) and the actual camera orientation
 *     (dim white needle) so you can see facing vs. mid-swing camera lag.
 *
 * Pure DOM + a 2D canvas, mirroring `Compass` (src/ui/compass.ts). No Pixi.
 */

import type { Camera } from '../render/camera';
import type { InputState } from '../sim/input';
import type { Player } from '../sim/player';
import type { GameWindow } from './window';

const SIZE = 116;
const CENTER = SIZE / 2;
const RING = SIZE / 2 - 10;

/** 8-way world directions (screen y-down, so North = (0,-1) = up). */
const DIRS: ReadonlyArray<{ name: string; dx: number; dy: number }> = [
  { name: 'N', dx: 0, dy: -1 }, { name: 'NE', dx: 1, dy: -1 },
  { name: 'E', dx: 1, dy: 0 }, { name: 'SE', dx: 1, dy: 1 },
  { name: 'S', dx: 0, dy: 1 }, { name: 'SW', dx: -1, dy: 1 },
  { name: 'W', dx: -1, dy: 0 }, { name: 'NW', dx: -1, dy: -1 },
];

function norm(dx: number, dy: number): [number, number] {
  if (dx === 0 && dy === 0) return [0, 0];
  const len = Math.hypot(dx, dy);
  return [dx / len, dy / len];
}

function dirName(dx: number, dy: number): string {
  return DIRS.find((d) => d.dx === dx && d.dy === dy)?.name ?? '·';
}

export class MovementDevPanel {
  private readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D;
  private readonly moveChips: Record<string, HTMLElement> = {};
  private readonly camChips: Record<string, HTMLElement> = {};
  private readonly readout: HTMLElement;

  constructor(win: GameWindow) {
    const root = document.createElement('div');
    root.className = 'mv-root';

    const mkRow = (label: string, keys: ReadonlyArray<[string, string]>, store: Record<string, HTMLElement>) => {
      const row = document.createElement('div');
      row.className = 'mv-krow';
      const lbl = document.createElement('span');
      lbl.className = 'mv-lbl';
      lbl.textContent = label;
      row.appendChild(lbl);
      for (const [code, glyph] of keys) {
        const chip = document.createElement('span');
        chip.className = 'mv-key';
        chip.textContent = glyph;
        store[code] = chip;
        row.appendChild(chip);
      }
      root.appendChild(row);
    };
    mkRow('LEGS', [['KeyW', 'W'], ['KeyA', 'A'], ['KeyS', 'S'], ['KeyD', 'D']], this.moveChips);
    mkRow('AIM', [['ArrowUp', '↑'], ['ArrowLeft', '←'], ['ArrowDown', '↓'], ['ArrowRight', '→']], this.camChips);

    this.canvas = document.createElement('canvas');
    this.canvas.width = SIZE;
    this.canvas.height = SIZE;
    this.canvas.className = 'mv-radar';
    root.appendChild(this.canvas);

    this.readout = document.createElement('div');
    this.readout.className = 'mv-readout';
    root.appendChild(this.readout);

    win.body.appendChild(root);
    const ctx = this.canvas.getContext('2d');
    if (!ctx) throw new Error('movementDevPanel: no 2d context');
    this.ctx = ctx;
  }

  update(input: InputState, player: Player, camera: Camera): void {
    for (const code in this.moveChips) this.moveChips[code]!.classList.toggle('on', input.held.has(code));
    for (const code in this.camChips) this.camChips[code]!.classList.toggle('on', input.held.has(code));

    const c = this.ctx;
    c.clearRect(0, 0, SIZE, SIZE);

    // Ring + cross.
    c.strokeStyle = 'rgba(140,200,240,0.35)';
    c.lineWidth = 1;
    c.beginPath();
    c.arc(CENTER, CENTER, RING, 0, Math.PI * 2);
    c.stroke();
    c.strokeStyle = 'rgba(140,200,240,0.12)';
    c.beginPath();
    c.moveTo(CENTER - RING, CENTER); c.lineTo(CENTER + RING, CENTER);
    c.moveTo(CENTER, CENTER - RING); c.lineTo(CENTER, CENTER + RING);
    c.stroke();

    const miX = player.moveIntentDx;
    const miY = player.moveIntentDy;

    // 8 direction slots; the current move direction lights up cyan.
    for (const d of DIRS) {
      const [nx, ny] = norm(d.dx, d.dy);
      const x = CENTER + nx * RING;
      const y = CENTER + ny * RING;
      const lit = d.dx === miX && d.dy === miY;
      c.fillStyle = lit ? '#22e6ff' : 'rgba(140,200,240,0.25)';
      c.beginPath();
      c.arc(x, y, lit ? 5 : 2.5, 0, Math.PI * 2);
      c.fill();
    }

    // Camera "up" needle — the world direction currently rendered screen-up
    // (headingForFacing inverse). Dim; lags facing while the camera swings.
    const camA = -Math.PI / 2 - camera.headingCurrent;
    c.strokeStyle = 'rgba(255,255,255,0.35)';
    c.lineWidth = 1;
    c.beginPath();
    c.moveTo(CENTER, CENTER);
    c.lineTo(CENTER + Math.cos(camA) * (RING - 2), CENTER + Math.sin(camA) * (RING - 2));
    c.stroke();

    // Legs orientation needle — green. Uses the RETAINED leg direction
    // (holds its heading while idle, unlike the move-intent stick).
    const [lx, ly] = norm(player.legDx, player.legDy);
    c.strokeStyle = '#66ff77';
    c.lineWidth = 2;
    c.beginPath();
    c.moveTo(CENTER, CENTER);
    c.lineTo(CENTER + lx * (RING - 10), CENTER + ly * (RING - 10));
    c.stroke();

    // Torso look/aim needle — amber (this is player.facingDx/Dy).
    const [fx, fy] = norm(player.facingDx, player.facingDy);
    c.strokeStyle = '#ffcf50';
    c.lineWidth = 2;
    c.beginPath();
    c.moveTo(CENTER, CENTER);
    c.lineTo(CENTER + fx * (RING - 6), CENTER + fy * (RING - 6));
    c.stroke();

    // Joystick "stick" to the current move direction — bright cyan.
    if (miX !== 0 || miY !== 0) {
      const [sx, sy] = norm(miX, miY);
      c.strokeStyle = '#22e6ff';
      c.lineWidth = 2;
      c.beginPath();
      c.moveTo(CENTER, CENTER);
      c.lineTo(CENTER + sx * RING, CENTER + sy * RING);
      c.stroke();
    }

    c.fillStyle = '#e0eef8';
    c.beginPath();
    c.arc(CENTER, CENTER, 2.5, 0, Math.PI * 2);
    c.fill();

    this.readout.textContent = `legs ${dirName(player.legDx, player.legDy)} · torso ${dirName(player.facingDx, player.facingDy)}`;
  }
}
